import { JSX, useEffect, useState } from "react";
import { isEqual } from "lodash";
import ConfidenceTierFilter from "@app/projects/servicing-provider/provider-claims/ConfidenceTierFilter";
import ProviderSearchRow from "@app/projects/servicing-provider/provider-claims/ProviderSearchRow";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { compareConfidenceTierSelections } from "@app/shared/confidence-tier/confidenceTierLookup";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { checkForEnter } from "@app/shared/utility/checkForEnter";
import { TableType } from "@app/viewable-fields/viewableFields";

interface ConfidenceTierSearchFiltersProps {
  clearSearch?: boolean;
  descriptiveText?: string;
  handleProviderSearch: (searchValue: string, selectedPgrConfidenceTiers: number[]) => void;
  lastSearchValue: string;
  lastSelectedConfidenceTierValues?: number[];
  tableType: TableType;
  updateChangeTracker: () => void;
  validLoadStatus: boolean;
}

const ConfidenceTierSearchFilters = (props: ConfidenceTierSearchFiltersProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedConfidenceTierValues, setSelectedConfidenceTierValues] = useState<DropdownItem<number>[]>([]);

  useEffect(() => {
    if (props.clearSearch) {
      setSearchValue("");
      props.updateChangeTracker();
    }
  }, [props.clearSearch]);

  const applyDisabled = (): boolean => {
    const confidenceTierValuesEqual = isEqual(
      [...(props.lastSelectedConfidenceTierValues ?? [])].sort(compareConfidenceTierSelections),
      selectedConfidenceTierValues.map(x => x.key).sort(compareConfidenceTierSelections)
    );

    return !(props.validLoadStatus && (searchValue !== (props.lastSearchValue ?? "") || !confidenceTierValuesEqual));
  };

  const clearDisabled = (): boolean => !(props.validLoadStatus && (searchValue !== "" || selectedConfidenceTierValues.length !== 0));

  const onApplyClick = (): void => {
    props.handleProviderSearch(searchValue, selectedConfidenceTierValues.map(x => x.key));
  };

  const onClearClick = (): void => {
    setSearchValue("");
    setSelectedConfidenceTierValues([]);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card card-static">
          <div className="card-body">
            <ProviderSearchRow
              applyDisabled={applyDisabled()}
              buttonAlignment="center"
              buttonTestIdPrefix="confidence-tier-search-filters__button"
              clearDisabled={clearDisabled()}
              onApply={onApplyClick}
              onClear={onClearClick}
              tableType={props.tableType}
            >
              <div className="col-md-5">
                <QESTextInput
                  descriptiveText={props.descriptiveText}
                  label="Provider Search"
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyUp={(e) => checkForEnter(e, () => { if (!applyDisabled()) onApplyClick(); })}
                  testId={`confidence-tier-search-filters__input--search-${props.tableType}`}
                  value={searchValue}
                />
              </div>
              <div className="col-md-2">
                <ConfidenceTierFilter
                  selectedValues={selectedConfidenceTierValues}
                  setSelectedValues={setSelectedConfidenceTierValues}
                  testId={`provider-claims-filters__multiselect--confidence-tier-${props.tableType}`}
                  title="Confidence Level"
                />
              </div>
            </ProviderSearchRow>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfidenceTierSearchFilters;
